import {
  COMPARE_MODES,
  DEFAULT_FILTERS,
  ENVIRONMENTS,
  RANGE_PRESETS,
  globalFiltersSchema,
  type CompareMode,
  type GlobalFilters,
  type RangePreset,
} from "./filters.js"

const has = <T extends string>(pool: readonly T[], v: string | null): v is T =>
  v != null && (pool as readonly string[]).includes(v)

export function parseFilters(params: URLSearchParams): GlobalFilters {
  const env = params.get("env")
  const tenants = params.get("tenants")
  const range = params.get("range")
  const from = params.get("from")
  const to = params.get("to")
  const gran = params.get("gran")
  const cmp = params.get("cmp")

  return globalFiltersSchema.parse({
    environments: has(ENVIRONMENTS, env) ? [env] : [],
    tenants: tenants ? tenants.split(",").filter(Boolean) : [],
    includeChurned: params.get("churned") === "1",
    range:
      from && to
        ? { from, to }
        : has(RANGE_PRESETS, range)
          ? { preset: range as RangePreset }
          : DEFAULT_FILTERS.range,
    granularity: has(["auto", "day", "week", "month"] as const, gran) ? gran : "auto",
    compareMode: has(COMPARE_MODES, cmp) ? (cmp as CompareMode) : "overlay",
    comparePrevious: params.get("pop") === "1",
  })
}

export function serializeFilters(filters: GlobalFilters): URLSearchParams {
  const p = new URLSearchParams()
  const [env] = filters.environments
  if (env) p.set("env", env)
  if (filters.tenants.length) p.set("tenants", filters.tenants.join(","))
  if (filters.includeChurned) p.set("churned", "1")

  if ("preset" in filters.range) {
    if (filters.range.preset !== "last30d") p.set("range", filters.range.preset)
  } else {
    p.set("from", filters.range.from)
    p.set("to", filters.range.to)
  }

  if (filters.granularity !== "auto") p.set("gran", filters.granularity)
  if (filters.compareMode !== "overlay") p.set("cmp", filters.compareMode)
  if (filters.comparePrevious) p.set("pop", "1")
  return p
}

export const sameUrl = (a: GlobalFilters, b: GlobalFilters): boolean =>
  serializeFilters(a).toString() === serializeFilters(b).toString()

export function mergeIntoParams(current: URLSearchParams, filters: GlobalFilters): URLSearchParams {
  const owned = ["env", "tenants", "churned", "range", "from", "to", "gran", "cmp", "pop", "prio"]
  const next = new URLSearchParams(current)
  for (const k of owned) next.delete(k)
  for (const [k, v] of serializeFilters(filters)) next.set(k, v)
  return next
}
